import { useMemo, useState, type ReactNode } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { MASKED_PREFIX, profileFieldErrors } from '@workflow/contracts'
import type { AccountProfile, IdentityType, PaymentMethod, SensitiveProfileField, SubmitProfileInput } from '@workflow/contracts'
import { Button, cn, DatePicker, Dropdown, Field, Segmented, TextArea, TextField } from '@workflow/ui'
import { errorSentence } from '@/lib/api/client'
import { useRevealMemberFields, useSubmitProfile, useUpdateMemberProfile } from '@/lib/api/hooks'
import { IDENTITY_TYPE_LABEL, PAYMENT_METHOD_LABEL, PROFILE_GROUPS, emptyProfileForm, isPaymentMethod, paymentFieldVisible } from './profileFields'
import type { ProfileFormState } from './profileFields'

/**
 * The profile form, shared by onboarding (first submit opens the review), by
 * the Account pane (keeping it current), and — with `admin` set — by the
 * person panel, where an administrator corrects someone's record. One source
 * of fields — `PROFILE_GROUPS` — so the form, the review and the file never
 * disagree.
 *
 * Validation is the server's schema, asked early: `profileFieldErrors` runs
 * the very contract the API parses with, field by field, in the browser's own
 * words — "This field is required.", "This is not a valid phone number." —
 * under the field that earned them. The pass begins at the first submit
 * attempt and then speaks as the typing continues; before that, an untried
 * form stays quiet. Whatever slips past still arrives as the server's
 * sentence, in place under the button.
 */
export function ProfileForm({
  initial,
  submitLabel = 'Submit profile',
  onSubmitted,
  onCancel,
  admin,
  guidedSteps = false,
  finalStep,
}: {
  /** The profile on file, when there is one. */
  initial?: AccountProfile | null
  submitLabel?: string
  onSubmitted?: (profile: AccountProfile) => void
  onCancel?: () => void
  /** Set when an administrator is filing on another person's behalf. */
  admin?: { memberId: string; name: string | null }
  /** Show one profile category at a time during first account setup. */
  guidedSteps?: boolean
  /** An optional final onboarding step, shown after validated profile details. */
  finalStep?: { title: string; content: ReactNode; pending?: boolean }
}) {
  const [form, setForm] = useState<ProfileFormState>(() =>
    initial
      ? {
          ...emptyProfileForm(),
          ...Object.fromEntries(
            Object.entries(emptyProfileForm()).map(([key]) => [key, initial[key as keyof AccountProfile] ?? '']),
          ),
          identityType: initial.identityType ?? 'nid',
          paymentMethod: isPaymentMethod(initial.paymentMethod ?? '') ? initial.paymentMethod as PaymentMethod : initial.paymentMethod ? 'custom' : '',
          paymentDetails: isPaymentMethod(initial.paymentMethod ?? '') ? initial.paymentDetails ?? '' : initial.paymentMethod ?? '',
        }
      : emptyProfileForm(),
  )
  const [name, setName] = useState(admin?.name ?? '')
  const [attempted, setAttempted] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const submitProfile = useSubmitProfile()
  const adminUpdate = useUpdateMemberProfile()
  const revealFields = useRevealMemberFields()
  const pending = admin ? adminUpdate.isPending : submitProfile.isPending
  const failure = admin ? adminUpdate.error : submitProfile.error

  const allErrors = useMemo(() => {
    if (!attempted) return {} as Partial<Record<string, string>>
    return {
      ...profileFieldErrors(form),
      ...(admin && !name.trim() ? { name: 'This field is required.' } : {}),
    }
  }, [attempted, form, name, admin])

  const stepCount = PROFILE_GROUPS.length + (guidedSteps && finalStep ? 1 : 0)
  const activeGroup = PROFILE_GROUPS[currentStep]
  const isFinalStep = guidedSteps && currentStep === PROFILE_GROUPS.length && Boolean(finalStep)
  const shownGroups = guidedSteps ? activeGroup ? [activeGroup] : [] : PROFILE_GROUPS
  const errors = allErrors

  const set = (key: keyof SubmitProfileInput, value: string) => setForm((current) => ({ ...current, [key]: value }))

  const setPaymentMethod = (paymentMethod: PaymentMethod) => setForm((current) => ({
    ...current,
    paymentMethod,
    paymentAccountNumber: '',
    paymentBankName: '',
    paymentDetails: '',
  }))

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    // Enter advances the current category; it must not submit unseen fields.
    if (guidedSteps && currentStep < stepCount - 1) {
      continueStep()
      return
    }
    if (pending || finalStep?.pending) return
    setAttempted(true)
    const failing = Object.keys({
      ...profileFieldErrors(form),
      ...(admin && !name.trim() ? { name: 'This field is required.' } : {}),
    })
    if (failing.length > 0) {
      const order = ['name', ...PROFILE_GROUPS.flatMap((group) => group.fields.map((field) => field.key))]
      const first = order.find((key) => failing.includes(key))
      if (first && guidedSteps) {
        const groupIndex = PROFILE_GROUPS.findIndex((group) => group.fields.some((field) => field.key === first))
        if (groupIndex >= 0) setCurrentStep(groupIndex)
        requestAnimationFrame(() => document.getElementById(`profile-${first}`)?.focus())
      } else if (first) document.getElementById(`profile-${first}`)?.focus()
      return
    }
    if (admin) {
      adminUpdate.mutate(
        {
          id: admin.memberId,
          input: {
            ...(name.trim() !== (admin.name ?? '') ? { name: name.trim() } : {}),
            ...(form as SubmitProfileInput),
            // A sensitive field still wearing the mask was never revealed —
            // it stays out of the write, and the record keeps what it holds.
            ...(String(form.identityNumber).startsWith(MASKED_PREFIX) ? {} : { identityNumber: form.identityNumber }),
            ...(String(form.paymentAccountNumber).startsWith(MASKED_PREFIX)
              ? {}
              : { paymentAccountNumber: form.paymentAccountNumber }),
          },
        },
        { onSuccess: (detail) => onSubmitted?.(((detail as { profile: AccountProfile | null }).profile)!) },
      )
      return
    }
    submitProfile.mutate(form as SubmitProfileInput, { onSuccess: (profile) => onSubmitted?.(profile as AccountProfile) })
  }

  const continueStep = () => {
    if (!activeGroup || pending) return
    setAttempted(true)
    const groupErrors = profileFieldErrors(form)
    const fieldOrder = activeGroup.fields
      .filter((field) => activeGroup.title !== 'Payment Information' || paymentFieldVisible(field.key, form.paymentMethod, form.paymentAccountNumber))
      .map((field) => field.key)
    const firstInvalid = fieldOrder.find((key) => groupErrors[key])
    if (firstInvalid) {
      document.getElementById(`profile-${firstInvalid}`)?.focus()
      return
    }
    setAttempted(false)
    setCurrentStep((step) => Math.min(step + 1, stepCount - 1))
  }

  return (
    // No rules between sections: every field already draws its own line, so
    // a section divider only doubles it. Titles and spacing do the grouping.
    <form onSubmit={submit} className="flex flex-col">
      {guidedSteps ? (
        <div className="pb-5">
          <div className="mb-2 flex items-center justify-between text-micro text-muted-foreground">
            <span>{isFinalStep ? 'AVAILABILITY' : 'PROFILE DETAILS'}</span>
            <span>STEP {currentStep + 1} OF {stepCount}</span>
          </div>
          <div
            role="progressbar"
            aria-label="Profile completion"
            aria-valuemin={1}
            aria-valuemax={stepCount}
            aria-valuenow={currentStep + 1}
            className="flex gap-1.5"
          >
            {[...PROFILE_GROUPS.map((group) => group.title), ...(finalStep ? [finalStep.title] : [])].map((title, index) => (
              <div key={title} className={cn('h-1 flex-1 rounded-full', index <= currentStep ? 'bg-foreground' : 'bg-hairline')} />
            ))}
          </div>
        </div>
      ) : null}

      {admin ? (
        <section className="flex flex-col gap-4 py-6 first:pt-0">
          <Field label="Name" htmlFor="profile-name" hint="The name the directory shows." error={errors.name}>
            <TextField id="profile-name" variant="underline" value={name} onChange={(event) => setName(event.target.value)} />
          </Field>
        </section>
      ) : null}

      {shownGroups.map((group) => (
        <section key={group.title} className="flex flex-col gap-4 py-6 first:pt-0">
          <p className="text-micro text-muted-foreground/80 uppercase">{group.title}</p>
          <div className={cn('grid gap-x-6 gap-y-5', !guidedSteps && 'sm:grid-cols-2')}>
            <AnimatePresence initial={false} mode="popLayout">
            {group.fields.filter((field) => group.title !== 'Payment Information' || paymentFieldVisible(field.key, form.paymentMethod, form.paymentAccountNumber)).map((field) => {
              const id = `profile-${field.key}`
              const value = String(form[field.key] ?? '')
              const hidden =
                admin && (field.key === 'identityNumber' || field.key === 'paymentAccountNumber')
                  ? value.startsWith(MASKED_PREFIX)
                  : false
              return (
                <motion.div key={field.key} className={field.wide && !guidedSteps ? 'sm:col-span-2' : undefined}
                  initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.16 }}>
                <Field
                  label={field.key === 'paymentDetails' && form.paymentMethod === 'custom' ? 'Custom payment details' : field.label}
                  htmlFor={id}
                  error={errors[field.key]}
                >
                  {field.kind === 'payment' ? (
                    <Dropdown<PaymentMethod>
                      id={id}
                      ariaLabel="Payment method"
                      menuWidth="compact"
                      className="w-full max-w-[18rem]"
                      value={isPaymentMethod(form.paymentMethod) ? form.paymentMethod : ''}
                      placeholder="Choose a payment method"
                      options={(Object.keys(PAYMENT_METHOD_LABEL) as PaymentMethod[]).map((method) => ({ value: method, label: PAYMENT_METHOD_LABEL[method] }))}
                      onChange={setPaymentMethod}
                    />
                  ) : field.kind === 'textarea' ? (
                    <TextArea id={id} variant="underline" aria-invalid={Boolean(errors[field.key]) || undefined} value={value} rows={2} placeholder={field.placeholder} onChange={(e) => set(field.key, e.target.value)} />
                  ) : field.kind === 'date' ? (
                    <DatePicker
                      id={id}
                      value={form.dateOfBirth ?? ''}
                      invalid={Boolean(errors[field.key])}
                      onChange={(iso) => set(field.key, iso)}
                    />
                  ) : field.kind === 'identity' ? (
                    <Segmented<IdentityType>
                      fullWidth
                      label="Document type"
                      value={(form.identityType as IdentityType) ?? 'nid'}
                      onChange={(identityType) => setForm((current) => ({ ...current, identityType }))}
                      options={(Object.keys(IDENTITY_TYPE_LABEL) as IdentityType[]).map((type) => ({
                        value: type,
                        label: IDENTITY_TYPE_LABEL[type],
                      }))}
                    />
                  ) : hidden ? (
                    <div className="flex items-center gap-2">
                      <TextField
                        id={id}
                        variant="underline"
                        value={value}
                        placeholder={field.placeholder}
                        onChange={(e) => set(field.key, e.target.value)}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        busy={revealFields.isPending}
                        onClick={() => {
                          if (!admin) return
                          revealFields.mutate(
                            { id: admin.memberId, fields: [field.key as SensitiveProfileField] },
                            {
                              onSuccess: (answer) => {
                                setForm((current) => ({
                                  ...current,
                                  ...Object.fromEntries(
                                    Object.entries(answer).map(([key, answerValue]) => [key, answerValue ?? '']),
                                  ),
                                }))
                              },
                            },
                          )
                        }}
                      >
                        Show
                      </Button>
                    </div>
                  ) : (
                    <TextField
                      id={id}
                      variant="underline"
                      aria-invalid={Boolean(errors[field.key]) || undefined}
                      value={value}
                      placeholder={field.placeholder}
                      onChange={(e) => set(field.key, e.target.value)}
                    />
                  )}
                </Field>
                </motion.div>
              )
            })}
            </AnimatePresence>
          </div>
        </section>
      ))}

      {isFinalStep && finalStep ? (
        <section className="py-6">
          {finalStep.content}
        </section>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3 py-6">
        <div>
          {guidedSteps && currentStep > 0 ? (
            <Button type="button" variant="ghost" onClick={() => { setAttempted(false); setCurrentStep((step) => step - 1) }}>
              Back
            </Button>
          ) : onCancel ? (
            <Button size="md" variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
        </div>
        {guidedSteps && currentStep < stepCount - 1 ? (
          <Button key="continue" type="button" variant="primary" onClick={continueStep}>
            Continue
          </Button>
        ) : (
          <Button key="submit" type="submit" variant="primary" busy={pending} disabled={Boolean(finalStep?.pending)}>
            {submitLabel}
          </Button>
        )}
        {failure ? <p className="text-caption text-danger">{errorSentence(failure)}</p> : null}
      </div>
    </form>
  )
}
